import { Check } from "lucide-react";
import { useScrollReveal } from "../lib/useScrollReveal";

const LEVELS = [
  {
    tag: "Level 01",
    title: "Beginners",
    subtitle:
      "No prior experience needed. Start from scratch — safely and confidently.",
    headerBg: "#1F4A38",
    covered: [
      "Foundational asanas with alignment correction",
      "Basic breathing techniques (pranayama)",
      "Flexibility and posture improvement",
      "Stress relief and relaxation practices",
      "Diet and lifestyle guidance",
    ],
    bestFor: "New students, homemakers, seniors, post-pregnancy recovery",
  },
  {
    tag: "Level 02",
    title: "Intermediate",
    subtitle:
      "Deepen your practice with advanced techniques and therapeutic programs.",
    headerBg: "#153326",
    covered: [
      "Advanced asana sequences and flow work",
      "Strength, mobility and core conditioning",
      "Weight management structured plan",
      "Meditation and mindfulness deepening",
      "Therapeutic programs for holistic wellness",
    ],
    bestFor: "Existing practitioners, weight loss goals, lifestyle transformation",
  },
];

export const Levels = () => {
  const ref = useScrollReveal();

  return (
    <section
      id="levels"
      data-testid="levels-section"
      ref={ref}
      className="relative py-24 md:py-36 bg-gradient-to-b from-[#F9F6F0] to-[#F1ECE0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
            Progressive Path
          </p>
          <h2
            data-testid="levels-heading"
            className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#153326] font-light leading-[1.05]"
          >
            Your Practice, <em className="italic">Your</em> Level
          </h2>
          <p className="reveal mt-6 text-[#2C5F4A]/80 font-light">
            Two carefully designed tiers, so you meet yourself exactly where you are.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {LEVELS.map((lvl, i) => (
            <article
              key={lvl.title}
              data-testid={`level-card-${i + 1}`}
              className="reveal rounded-[28px] overflow-hidden bg-white shadow-[0_14px_46px_rgba(31,74,56,0.08)] hover:shadow-[0_22px_60px_rgba(31,74,56,0.14)] transition-all duration-500 hover:-translate-y-1 flex flex-col"
            >
              <div
                className="p-8 md:p-10 relative grain"
                style={{ backgroundColor: lvl.headerBg, color: "#F9F6F0" }}
              >
                <span className="inline-block px-3 py-1 rounded-full bg-white/15 border border-white/20 text-[11px] tracking-[0.25em] uppercase mb-5">
                  {lvl.tag}
                </span>
                <h3 className="font-serif-display text-4xl md:text-5xl font-medium">
                  {lvl.title}
                </h3>
                <p className="mt-4 text-[#F9F6F0]/85 max-w-md leading-relaxed">
                  {lvl.subtitle}
                </p>
              </div>

              <div className="p-8 md:p-10 flex-1 flex flex-col">
                <p className="text-[11px] tracking-[0.3em] uppercase text-[#D4A24C] font-semibold mb-5">
                  What's Covered
                </p>
                <ul className="space-y-4">
                  {lvl.covered.map((c) => (
                    <li key={c} className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#1F4A38]/10 text-[#1F4A38] shrink-0">
                        <Check size={13} strokeWidth={2.5} />
                      </span>
                      <span className="text-[#153326]">{c}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-8 border-t border-[#1F4A38]/10 mt-8">
                  <p className="text-sm text-[#2C5F4A]">
                    <span className="text-[#2C5F4A]/70">Best for: </span>
                    <span className="font-semibold text-[#153326]">{lvl.bestFor}</span>
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Levels;
